import type { GymOwnerStatusName } from '@steado/admin-contract/gyms';

import type { ToneName } from '@/components/table/tone';
import { formatDate } from '@/lib/format';

/**
 * Where a gym owner's account stands: invited by Steado and not in yet, or
 * active. The list's badge and the gym page's status line are worded here
 * once, so "link expired" means the same thing on both.
 */

interface OwnerInvite {
  readonly status: GymOwnerStatusName;
  readonly inviteExpiresAt: string | null;
}

/** The newest invite's link is past its time. Only an invited owner has one to expire. */
export function inviteExpired(owner: OwnerInvite, now: Date = new Date()): boolean {
  return (
    owner.status === 'invited' && owner.inviteExpiresAt !== null && new Date(owner.inviteExpiresAt) <= now
  );
}

/** The list's badge for an owner who has not accepted their invite; null once active. */
export function ownerInviteBadge(
  owner: OwnerInvite,
  now: Date = new Date(),
): { label: string; tone: ToneName } | null {
  if (owner.status === 'active') return null;
  return inviteExpired(owner, now)
    ? { label: 'invite expired', tone: 'warn' }
    : { label: 'owner invited', tone: 'info' };
}

/** "Invited · link expires 01 Nov 26", "Invited · link expired …", or "Active since 03 Oct 26". */
export function ownerStatusLine(
  owner: OwnerInvite & { readonly activeSince: string | null },
  now: Date = new Date(),
): string {
  if (owner.status === 'active') {
    return owner.activeSince === null ? 'Active' : `Active since ${formatDate(owner.activeSince)}`;
  }
  if (owner.inviteExpiresAt === null) return 'Invited · no link sent yet';
  const date = formatDate(owner.inviteExpiresAt);
  return inviteExpired(owner, now) ? `Invited · link expired ${date}` : `Invited · link expires ${date}`;
}
